import {
  Authenticator,
  Image,
  Text,
  CheckboxField,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { signIn } from "aws-amplify/auth";
import { PropsWithChildren } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/sharp-solid-svg-icons";
import StaticHeader from "../../layout/header/static-header";

import styles from "./amplify-authenticator.module.scss";

type LoginMechanisms = Parameters<typeof Authenticator>[0]["loginMechanisms"];

const Footer = () => (
  <Text variation="tertiary" className={styles.copyright}>
    {"© 2025 All rights reserved."}
  </Text>
);

const ForcePasswordAgreeToTermsAndPrivacyPolicy = () => (
  <>
    <Authenticator.ForceNewPassword.FormFields />
    <CheckboxField
      name="custom:agreeToTerms"
      value="agree"
      isRequired
      label={
        <>
          {"I agree to the"}{" "}
          <a
            href="https://www.getclaimable.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            {"Terms of Service"}
          </a>{" "}
          {"and"}{" "}
          <a
            href="https://www.getclaimable.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            {"Privacy Policy"}
          </a>
        </>
      }
    />
  </>
);

const SignInHeader = () => <h1 className={styles.signInHeader}>{"Sign in"}</h1>;

const services = {
  handleSignIn: (input: Parameters<typeof signIn>[0]) =>
    signIn({ ...input, password: input.password?.trim() }),
};

const ClaimableAuthenticator = ({ children }: PropsWithChildren) => {
  const loginMechanisms: LoginMechanisms = ["email"];
  const themeComponents = {
    Footer,
    ForceNewPassword: {
      FormFields: ForcePasswordAgreeToTermsAndPrivacyPolicy,
    },
    SignIn: {
      Header: SignInHeader,
    },
  };

  const { authStatus } = useAuthenticator();

  if (authStatus === "authenticated") {
    return (
      <div className={styles.claimableAuthenticator}>
        <Authenticator
          hideSignUp
          loginMechanisms={loginMechanisms}
          components={themeComponents}
          services={services}
        >
          {children}
        </Authenticator>
      </div>
    );
  }

  return (
    <div className={styles.claimableAuthenticator}>
      <div className={styles.loginPageWrapper}>
        <StaticHeader />
        <div className={styles.mainContentContainer}>
          <div className={styles.authenticatorComponentContainer}>
            <div className={styles.authenticatorComponentSubheading}>
              {"Log in"}
            </div>
            <div className={styles.authenticatorComponentHeading}>
              {"Claimable for Providers"}
            </div>
            <Authenticator
              hideSignUp
              loginMechanisms={loginMechanisms}
              components={themeComponents}
              services={services}
            >
              {children}
            </Authenticator>
          </div>
          <div className={styles.loginPageSideBar}>
            <div className={styles.heroContainer}>
              <Image
                alt="Provider-Computer"
                src="/images/login-page-computer.svg"
                height={280}
              />
            </div>

            <div className={styles.sidebarQuoteContainer}>
              <div className={styles.sidebarQuoteTitle}>
                {"Patients love us:"}
              </div>
              <div className={styles.sidebarInfoText}>
                <div>
                  {
                    "This service was so good that I was approved within 48 hours. Matter of fact, I know I had at least three different people contacting me letting me know that I was approved for the medicine that they had previously denied me."
                  }
                </div>
                <Image
                  alt="5-Star Review"
                  src="/images/login-page-quote-stars.svg"
                  height={16}
                />
                <div>{"Linda Tucker"}</div>
              </div>
            </div>

            <div className={styles.sidebarSalesPointsContainer}>
              <div className={styles.sidebarSalesPointsHeading}>
                {"Our secure & HIPAA-compliant tool allows you to:"}
              </div>
              <ul className={styles.sidebarSalesPoints}>
                <li>
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className={styles.salesPointsIcon}
                  />
                  {"Refer patients for personalized & expert-generated appeals"}
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className={styles.salesPointsIcon}
                  />
                  {
                    "Guarantee appeals delivery to key decision makers with shipping and fax included"
                  }
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className={styles.salesPointsIcon}
                  />
                  {
                    "Track appeals status and outcomes so you’re always informed"
                  }
                </li>
                <li>
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className={styles.salesPointsIcon}
                  />
                  {"Invite team members to manage referrals together"}
                </li>
              </ul>
            </div>
            <div className={styles.sidebarFooter}>
              <div className={styles.footerPrimaryText}>
                {"Your denied healthcare claims, handled"}
              </div>
              <div className={styles.footerTermsAndPrivacyText}>
                <a href="https://www.getclaimable.com/partner-terms-of-service">
                  {"Terms of service"}
                </a>
                {"|"}
                <a href="https://www.getclaimable.com/privacy-policy">
                  {"Privacy Policy"}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimableAuthenticator;
